import type { Config } from '@backstage/config';
import * as Sentry from '@sentry/react';

let initialized = false;

export function initSentry(config: Config): boolean {
  if (initialized) return true;
  const enabled = config.getOptionalBoolean('sentry.enabled') ?? false;
  if (!enabled) return false;
  const dsn = config.getOptionalString('sentry.frontend.dsn');
  if (!dsn) {
    // Fires from an ApiFactory before any ErrorApi is available.
    // eslint-disable-next-line no-console
    console.warn(
      'Sentry frontend enabled but sentry.frontend.dsn is missing — error reporting disabled.',
    );
    return false;
  }
  const environment = config.getOptionalString('sentry.environment');
  const release = config.getOptionalString('sentry.release');
  Sentry.init({ dsn, environment, release, tracesSampleRate: 0 });
  initialized = true;
  return true;
}

/**
 * Test-only escape hatch to reset the module-level `initialized` flag.
 * Mirrors the backend's `__resetForTesting` in packages/backend/src/sentry.ts.
 */
export function __resetForTesting(): void {
  initialized = false;
}

export interface ErrorApiLike {
  post(error: Error, context?: unknown): void;
  error$(): unknown;
}

export function wrapErrorApi<T extends ErrorApiLike>(delegate: T): T {
  return new Proxy(delegate, {
    get(target, prop, receiver) {
      if (prop === 'post') {
        return (error: Error, context?: unknown) => {
          Sentry.captureException(error, {
            extra: context as Record<string, unknown>,
          });
          target.post(error, context);
        };
      }
      return Reflect.get(target, prop, receiver);
    },
  }) as T;
}
